import * as THREE from 'three';
import { isPointInPoly, isPointOnLine } from './geometry.js';

export const POLYGON_EDIT_VERTEX_TOL = 2;
export const POLYGON_EDIT_EDGE_TOL = 2;
export const POLYGON_EDIT_INTERIOR_EDGE_TOL = 0.25;

export const toPlanarPoint = (point) => {
  if (!point) return null;
  if (Array.isArray(point)) {
    const x = Number(point[0]);
    const y = Number(point[1]);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
  }
  const x = Number(point.x);
  const y = Number(point.z ?? point.y);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
};

export const planarFromScenePolygon = (poly) => {
  if (!Array.isArray(poly)) return [];
  const ring = poly.map((p) => toPlanarPoint(p)).filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y));
  if (ring.length > 1 && pointsNear(ring[0], ring[ring.length - 1])) {
    ring.pop();
  }
  return ring;
};

export const sceneFromPlanar = (poly) =>
  (Array.isArray(poly) ? poly : []).map((p) => new THREE.Vector3(p.x, 0, p.y));

export const pointsNear = (a, b, tol = POLYGON_EDIT_VERTEX_TOL) => {
  if (!a || !b) return false;
  return Math.hypot(a.x - b.x, a.y - b.y) <= tol;
};

export const isClosedPolygonType = (type) => !type || type === 'Polygon';

export const polygonArea = (poly) => {
  const ring = planarFromScenePolygon(poly);
  if (ring.length < 3) return 0;
  const vecs = ring.map((p) => new THREE.Vector3(p.x, p.y, 0));
  return Math.abs(THREE.ShapeUtils.area(vecs));
};

export const locateOnBoundary = (point, poly, vertexTol = POLYGON_EDIT_VERTEX_TOL, edgeTol = POLYGON_EDIT_EDGE_TOL) => {
  const ring = planarFromScenePolygon(poly);
  const p = toPlanarPoint(point);
  if (!p || ring.length < 2) return null;
  for (let i = 0; i < ring.length; i += 1) {
    if (pointsNear(p, ring[i], vertexTol)) {
      return { kind: 'vertex', vertexIndex: i, edgeIndex: null, projected: ring[i], polyLen: ring.length };
    }
  }
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    if (isPointOnLine(p, a, b, edgeTol)) {
      return { kind: 'edge', vertexIndex: null, edgeIndex: i, projected: projectToSegment(p, a, b), polyLen: ring.length };
    }
  }
  return null;
};

export const isOnBoundary = (point, poly, vertexTol = POLYGON_EDIT_VERTEX_TOL, edgeTol = POLYGON_EDIT_EDGE_TOL) =>
  Boolean(locateOnBoundary(point, poly, vertexTol, edgeTol));

export const isInteriorPoint = (point, poly, edgeTol = POLYGON_EDIT_INTERIOR_EDGE_TOL) => {
  const ring = planarFromScenePolygon(poly);
  const p = toPlanarPoint(point);
  if (!p || ring.length < 3) return false;
  if (isOnBoundary(p, ring, edgeTol, edgeTol)) return false;
  return isPointInPoly(p, ring, false, edgeTol);
};

export const findInteriorPolygonIndex = (point, polygons, types) => {
  if (!Array.isArray(polygons)) return -1;
  for (let i = 0; i < polygons.length; i += 1) {
    if (!isClosedPolygonType(types?.[i])) continue;
    if (isInteriorPoint(point, polygons[i])) return i;
  }
  return -1;
};

export const findBoundaryHits = (point, polygons, types) => {
  if (!Array.isArray(polygons)) return [];
  const hits = [];
  for (let i = 0; i < polygons.length; i += 1) {
    if (!isClosedPolygonType(types?.[i])) continue;
    const loc = locateOnBoundary(point, polygons[i]);
    if (loc) hits.push({ ...loc, polyIndex: i });
  }
  return hits;
};

const projectAlong = (p, a, b) => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 <= Number.EPSILON) return 0;
  return ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
};

export const findCollinearOverlap = (a1, a2, b1, b2, tol = POLYGON_EDIT_EDGE_TOL) => {
  if (!isPointOnLine(b1, a1, a2, tol) && !isPointOnLine(a1, b1, b2, tol)) return null;
  if (!isPointOnLine(b2, a1, a2, tol) && !isPointOnLine(a2, b1, b2, tol)) return null;
  const aOnB = isPointOnLine(a1, b1, b2, tol) && isPointOnLine(a2, b1, b2, tol);
  const bOnA = isPointOnLine(b1, a1, a2, tol) && isPointOnLine(b2, a1, a2, tol);
  if (!aOnB && !bOnA) {
    const mixed =
      (isPointOnLine(b1, a1, a2, tol) || isPointOnLine(b2, a1, a2, tol)) &&
      (isPointOnLine(a1, b1, b2, tol) || isPointOnLine(a2, b1, b2, tol));
    if (!mixed) return null;
  }
  const tA1 = 0;
  const tA2 = 1;
  const tB1 = projectAlong(b1, a1, a2);
  const tB2 = projectAlong(b2, a1, a2);
  const lo = Math.max(Math.min(tA1, tA2), Math.min(tB1, tB2));
  const hi = Math.min(Math.max(tA1, tA2), Math.max(tB1, tB2));
  const abx = a2.x - a1.x;
  const aby = a2.y - a1.y;
  const overlapLen = Math.hypot(abx, aby) * (hi - lo);
  if (hi - lo <= 1e-4 || overlapLen <= tol) return null;
  return {
    p: { x: a1.x + lo * abx, y: a1.y + lo * aby },
    q: { x: a1.x + hi * abx, y: a1.y + hi * aby },
    reversed: (tB2 - tB1) * (tA2 - tA1) < 0
  };
};

export const findSharedEdge = (polyA, polyB, tol = POLYGON_EDIT_VERTEX_TOL) => {
  const a = planarFromScenePolygon(polyA);
  const b = planarFromScenePolygon(polyB);
  if (a.length < 3 || b.length < 3) return null;
  for (let i = 0; i < a.length; i += 1) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j += 1) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      const same = pointsNear(a1, b1, tol) && pointsNear(a2, b2, tol);
      const reversed = pointsNear(a1, b2, tol) && pointsNear(a2, b1, tol);
      if (same || reversed) return { i, j, reversed: Boolean(reversed) };
    }
  }
  for (let i = 0; i < a.length; i += 1) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j += 1) {
      const overlap = findCollinearOverlap(a1, a2, b[j], b[(j + 1) % b.length], POLYGON_EDIT_EDGE_TOL);
      if (overlap) return { i, j, reversed: overlap.reversed, overlap };
    }
  }
  return null;
};

export const insertPointOnRing = (ring, point) => {
  const loc = locateOnBoundary(point, ring);
  if (!loc || loc.kind === 'vertex') return ring.map((p) => ({ ...p }));
  const next = ring.map((p) => ({ ...p }));
  next.splice(loc.edgeIndex + 1, 0, { ...loc.projected });
  return next;
};

export const mapRingEdgeLabels = (ring, sources, tol = POLYGON_EDIT_VERTEX_TOL) => {
  const dest = planarFromScenePolygon(ring);
  return dest.map((p, i) => {
    const q = dest[(i + 1) % dest.length];
    for (const src of sources || []) {
      const srcRing = planarFromScenePolygon(src.ring);
      const srcLines = Array.isArray(src.lines) ? src.lines : [];
      for (let e = 0; e < srcRing.length; e += 1) {
        const a = srcRing[e];
        const b = srcRing[(e + 1) % srcRing.length];
        const same =
          (pointsNear(p, a, tol) && pointsNear(q, b, tol)) || (pointsNear(p, b, tol) && pointsNear(q, a, tol));
        const sub =
          isPointOnLine(p, a, b, POLYGON_EDIT_EDGE_TOL) && isPointOnLine(q, a, b, POLYGON_EDIT_EDGE_TOL);
        if (same || sub) return srcLines[e] || '';
      }
    }
    return '';
  });
};

export const isSameBoundaryEdge = (hitA, hitB) => {
  if (!hitA || !hitB || hitA.polyIndex !== hitB.polyIndex) return false;
  const n = hitA.polyLen || hitB.polyLen;
  if (!n) return false;
  if (hitA.edgeIndex != null && hitB.edgeIndex != null) {
    return hitA.edgeIndex === hitB.edgeIndex;
  }
  if (hitA.vertexIndex != null && hitB.vertexIndex != null) {
    const d = Math.abs(hitA.vertexIndex - hitB.vertexIndex);
    return d === 1 || d === n - 1;
  }
  const vertex = hitA.vertexIndex != null ? hitA : hitB;
  const edge = hitA.edgeIndex != null ? hitA : hitB;
  if (vertex.vertexIndex == null || edge.edgeIndex == null) return false;
  return edge.edgeIndex === vertex.vertexIndex || edge.edgeIndex === (vertex.vertexIndex - 1 + n) % n;
};

export const isValidMergeTarget = (point, polygons, types, firstIndex = -1) => {
  const index = findInteriorPolygonIndex(point, polygons, types);
  if (index < 0) return false;
  if (firstIndex < 0) return true;
  if (index === firstIndex) return false;
  return Boolean(findSharedEdge(polygons[firstIndex], polygons[index]));
};

export const isValidSplitTarget = (point, polygons, types, firstHits = null) => {
  const hits = findBoundaryHits(point, polygons, types);
  if (!hits.length) return false;
  if (!firstHits?.length) return true;
  const startSet = new Set(firstHits.map((hit) => hit.polyIndex));
  return hits.some((hit) => {
    if (!startSet.has(hit.polyIndex)) return false;
    const startHit = firstHits.find((item) => item.polyIndex === hit.polyIndex);
    return startHit && !isSameBoundaryEdge(startHit, hit);
  });
};

export const snapToSplitBoundary = (point, polygons, types) => {
  const hits = findBoundaryHits(point, polygons, types);
  if (!hits.length) return null;
  const planar = toPlanarPoint(point);
  let best = hits[0];
  let bestDist = Infinity;
  hits.forEach((hit) => {
    const dist = Math.hypot(planar.x - hit.projected.x, planar.y - hit.projected.y);
    if (dist < bestDist) {
      best = hit;
      bestDist = dist;
    }
  });
  return {
    point: new THREE.Vector3(best.projected.x, 0, best.projected.y),
    hits
  };
};

const isEdgeOnRing = (p, q, ring, tol = POLYGON_EDIT_VERTEX_TOL) => {
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    if ((pointsNear(p, a, tol) && pointsNear(q, b, tol)) || (pointsNear(p, b, tol) && pointsNear(q, a, tol))) {
      return true;
    }
    if (isPointOnLine(p, a, b, POLYGON_EDIT_EDGE_TOL) && isPointOnLine(q, a, b, POLYGON_EDIT_EDGE_TOL)) {
      return true;
    }
  }
  return false;
};

const collectOverlapPoints = (a, b) => {
  const points = [];
  for (let i = 0; i < a.length; i += 1) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j += 1) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      const same = pointsNear(a1, b1) && pointsNear(a2, b2);
      const reversed = pointsNear(a1, b2) && pointsNear(a2, b1);
      if (same || reversed) continue;
      const overlap = findCollinearOverlap(a1, a2, b1, b2);
      if (overlap) {
        points.push(overlap.p, overlap.q);
      }
    }
  }
  return points;
};

const ringEdges = (ring) =>
  ring.map((p, i) => ({
    p: { ...p },
    q: { ...ring[(i + 1) % ring.length] }
  }));

const stitchEdges = (edges) => {
  const unused = edges.map((edge) => ({ ...edge, used: false }));
  const rings = [];
  unused.forEach((start, startIndex) => {
    if (start.used) return;
    const ring = [{ ...start.p }];
    start.used = true;
    let current = start.q;
    for (let step = 0; step < unused.length + 1; step += 1) {
      if (pointsNear(current, ring[0])) break;
      let next = unused.find((edge) => !edge.used && pointsNear(edge.p, current));
      if (next) {
        ring.push({ ...current });
        current = next.q;
        next.used = true;
        continue;
      }
      next = unused.find((edge) => !edge.used && pointsNear(edge.q, current));
      if (!next) break;
      ring.push({ ...current });
      current = next.p;
      next.used = true;
    }
    if (ring.length >= 3) rings.push(ring);
  });
  if (!rings.length) return null;
  return rings.reduce((best, ring) => (polygonArea(ring) > polygonArea(best) ? ring : best));
};

export const mergePolygons = (polyA, polyB) => {
  let a = planarFromScenePolygon(polyA);
  let b = planarFromScenePolygon(polyB);
  if (!findSharedEdge(a, b)) return null;
  collectOverlapPoints(a, b).forEach((point) => {
    a = insertPointOnRing(a, point);
    b = insertPointOnRing(b, point);
  });
  const leftover = [
    ...ringEdges(a).filter((edge) => !isEdgeOnRing(edge.p, edge.q, b)),
    ...ringEdges(b).filter((edge) => !isEdgeOnRing(edge.p, edge.q, a))
  ];
  if (!leftover.length) return null;
  const merged = stitchEdges(leftover);
  return merged && merged.length >= 3 ? merged : null;
};

export const simplifyCollinearRing = (ring, lines, extras = []) => {
  const dest = planarFromScenePolygon(ring);
  const labels = Array.isArray(lines) ? lines.slice(0, dest.length) : [];
  while (labels.length < dest.length) labels.push('');
  const extraLists = (Array.isArray(extras) ? extras : []).map((list) => {
    const next = Array.isArray(list) ? list.slice(0, dest.length) : [];
    while (next.length < dest.length) next.push(undefined);
    return next;
  });
  let changed = true;
  while (changed && dest.length > 3) {
    changed = false;
    for (let i = 0; i < dest.length; i += 1) {
      const prev = dest[(i - 1 + dest.length) % dest.length];
      const curr = dest[i];
      const next = dest[(i + 1) % dest.length];
      const incoming = labels[(i - 1 + dest.length) % dest.length];
      const outgoing = labels[i];
      if (String(incoming ?? '') !== String(outgoing ?? '')) continue;
      if (!isPointOnLine(curr, prev, next, POLYGON_EDIT_EDGE_TOL)) continue;
      dest.splice(i, 1);
      labels.splice(i, 1);
      extraLists.forEach((list) => list.splice(i, 1));
      changed = true;
      break;
    }
  }
  return { ring: dest, lines: labels, extras: extraLists };
};

export const splitPolygon = (poly, pointA, pointB) => {
  const ring = planarFromScenePolygon(poly);
  const locA = locateOnBoundary(pointA, ring);
  const locB = locateOnBoundary(pointB, ring);
  if (!locA || !locB || isSameBoundaryEdge({ ...locA, polyIndex: 0 }, { ...locB, polyIndex: 0 })) {
    return null;
  }
  const verts = ring.map((p) => ({ ...p }));
  const inserts = [];
  if (locA.kind === 'edge') inserts.push({ edgeIndex: locA.edgeIndex, point: locA.projected, key: 'a' });
  if (locB.kind === 'edge') inserts.push({ edgeIndex: locB.edgeIndex, point: locB.projected, key: 'b' });
  inserts.sort((x, y) => y.edgeIndex - x.edgeIndex);
  inserts.forEach((item) => {
    verts.splice(item.edgeIndex + 1, 0, { ...item.point });
  });
  const i1 = verts.findIndex((p) => pointsNear(p, locA.projected));
  const i2 = verts.findIndex((p, idx) => idx !== i1 && pointsNear(p, locB.projected));
  if (i1 < 0 || i2 < 0 || i1 === i2) return null;
  const walk = (from, to) => {
    const path = [];
    let idx = from;
    while (true) {
      path.push({ ...verts[idx] });
      if (idx === to) break;
      idx = (idx + 1) % verts.length;
      if (path.length > verts.length + 1) return [];
    }
    return path;
  };
  const first = walk(i1, i2);
  const second = walk(i2, i1);
  if (first.length < 3 || second.length < 3) return null;
  return [first, second];
};

export const averagePlaneAttributes = (items, areas) => {
  const list = Array.isArray(items) ? items : [];
  const weights = Array.isArray(areas) ? areas : list.map(() => 1);
  const pickLinear = (key) => {
    let sum = 0;
    let weight = 0;
    list.forEach((item, i) => {
      const value = Number(item?.[key]);
      if (!Number.isFinite(value)) return;
      const w = Math.max(Number(weights[i]) || 0, 0);
      sum += value * w;
      weight += w;
    });
    return weight > 0 ? sum / weight : undefined;
  };
  const pickCircular = (key) => {
    let x = 0;
    let y = 0;
    let weight = 0;
    list.forEach((item, i) => {
      const value = Number(item?.[key]);
      if (!Number.isFinite(value)) return;
      const w = Math.max(Number(weights[i]) || 0, 0);
      const rad = (value * Math.PI) / 180;
      x += Math.cos(rad) * w;
      y += Math.sin(rad) * w;
      weight += w;
    });
    if (weight <= 0) return undefined;
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  };
  const result = {};
  const pitch = pickLinear('pitch');
  const azimuth = pickCircular('azimuth');
  const height = pickLinear('height');
  const eaveHeight = pickLinear('eaveHeight');
  if (pitch !== undefined) result.pitch = pitch;
  if (azimuth !== undefined) result.azimuth = azimuth;
  if (height !== undefined) result.height = height;
  if (eaveHeight !== undefined) result.eaveHeight = eaveHeight;
  if (result.height === undefined && result.eaveHeight !== undefined) result.height = result.eaveHeight;
  if (result.eaveHeight === undefined && result.height !== undefined) result.eaveHeight = result.height;
  return result;
};

const projectToSegment = (p, a, b) => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 <= Number.EPSILON) return { ...a };
  const t = Math.min(1, Math.max(0, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2));
  return { x: a.x + abx * t, y: a.y + aby * t };
};
